// ============================================================
// THE LAST TURN, AS THIS SCREEN SAW IT (plan design-gaps, Z6; the session
// page's LAST TURN scrub; ruled: it replays on this screen only). What a
// screen saw during a turn - the attacks' impacts and the moves' walks, each
// stamped when THIS screen first saw it - kept as offsets from the turn's
// first; a replay hands them back as they fall due, restamped, so the beats
// and walks play again over the figures as they stand now.
//
// Pure. Zero React dependency. Milliseconds.
// ============================================================

/** A thing this screen saw, and when (Date.now, this screen's clock). */
export interface Seen<T> {
  item: T;
  seen: number;
}

/** A turn kept for replay: each thing at its offset from the turn's first, and how long the replay runs. */
export interface LastTurn<I, W> {
  impacts: { item: I; offset: number }[];
  walks: { item: W; offset: number }[];
  span: number;
}

/** How long a replay runs on after its last beat starts: a beat's own length, near enough. */
export const REPLAY_TAIL_MS = 1500;

/** The turn kept from what was seen during it, or null when nothing was. */
export function lastTurnOf<I, W>(impacts: readonly Seen<I>[], walks: readonly Seen<W>[]): LastTurn<I, W> | null {
  const seen = [...impacts, ...walks].map((s) => s.seen);
  if (!seen.length) return null;
  const from = Math.min(...seen);
  const at = <T>(s: Seen<T>) => ({ item: s.item, offset: s.seen - from });
  return { impacts: impacts.map(at), walks: walks.map(at), span: Math.max(...seen) - from + REPLAY_TAIL_MS };
}

/** What has fallen due `elapsed` ms into a replay, each with its offset (the replay restamps it from that). */
export function dueAt<I, W>(turn: LastTurn<I, W>, elapsed: number) {
  return {
    impacts: turn.impacts.filter((s) => s.offset <= elapsed),
    walks: turn.walks.filter((s) => s.offset <= elapsed),
  };
}
